import { combineReducers } from "redux";
import { AgentSearch, AgentSelectionMap, AgentSelection, AgentOffices, SubtreeStateMap, AgentSearchState, OfficeSelection, SubtreeState } from "app/pages/DiscountEditor/modules/Agent/state";
import { Action, ActionType } from "app/pages/DiscountEditor/actionTypes";
import { AgentSelectionMode } from "app/shared/model/AgentSelectionMode";

const defaultSearch : AgentSearch = {
    searchTerm: '',
    results: [],
    searchState: AgentSearchState.NoTerm
}

function search(state: AgentSearch|undefined, action: Action) : AgentSearch {
    if (state == undefined)
        state = defaultSearch;

    switch(action.type) {
        case ActionType.AgentSearchChanged:
            return {
                results: [],
                searchTerm: action.searchTerm,
                searchState: action.searchTerm.trim() == ''
                    ? AgentSearchState.NoTerm
                    : AgentSearchState.Searching
            }

        case ActionType.AgentSearchResultsLoaded:
            if (state.searchTerm !== action.searchTerm)
                return state;
            return {
                results: action.results,
                searchTerm: action.searchTerm,
                searchState: AgentSearchState.Loaded
            };

        case ActionType.AgentSearchFailed:
            return {
                results: [],
                searchState: AgentSearchState.Failed,
                searchTerm: state.searchTerm
            }

        default:
            return state;
    }
}

function agents(state: AgentSelectionMap|undefined, action: Action) : AgentSelectionMap {
    if (!state)
        state = {};

    switch(action.type) {
        case ActionType.PageLoaded:
        {
            if (action.discount.agentsFilter.selectionMode === AgentSelectionMode.AllAgents)
                return state;

            const newState  : AgentSelectionMap = {};

            action.discount.agentsFilter.agents.forEach(a => {
                let entity = action.data.agents.find(rec => rec.id == a.agentId);
                if (entity == undefined) {
                    return;
                }

                let selection : AgentSelection = {
                    agent: {
                        id: entity.id,
                        name: entity.name,
                        agentName: entity.name,
                        officeName: entity.offices.map((o: { name: string; }) => o.name).join(', '),
                        ownerFullName: entity.ownerFullName,
                        ownerEmail: entity.ownerEmail,
                        countryName: entity.countryName,
                        countryIsoCode: entity.countryIsoCode,
                        classification: entity.classification,
                        status: entity.status
                    },
                    isChecked: false,
                    isIndeterminate: false
                }
                newState[a.agentId] = selection;
                if (a.allOffices) {
                    selection.isChecked = true;
                }
                else {
                    const selected = new Set(a.offices);
                    selection.isChecked = !entity.offices.find(o => !selected.has(o.id))
                    selection.isIndeterminate = !selection.isChecked && a.offices.length > 0;
                }

            })
            

            return newState;
        }

        case ActionType.AgentSearchResultSelected:
            if (state[action.agent.id])
                return state;
            const newSelection : AgentSelection = {
                isChecked: true,
                isIndeterminate: false,
                agent: action.agent
            }
            return {
                ... state,
                [action.agent.id]: newSelection
            };

        case ActionType.ChangeAgentSelection:
        {
            const toReplace = state[action.id];
            if (toReplace == undefined)
                return state;
            const agent = toReplace.agent;

            return {
                ... state,
                [action.id]: {
                    isChecked: action.isSelected,
                    isIndeterminate: false,
                    agent: agent
                }
            }
        }

        case ActionType.ChangeAgentOfficeSelection:
        {
            const toReplace = state[action.agentId];
            if (toReplace == undefined)
                return state;
            const agent = toReplace.agent;

            return {
                ... state,
                [action.agentId]: {
                    isChecked: !!(action.isAgentSelected),
                    isIndeterminate: action.isAgentSelected === undefined,
                    agent: agent
                }
            }
        }

        default:
            return state;
    }
}

const defaultAgentOffices : AgentOffices = {
    idMap: {},
    byAgent: {}
};

function offices(state: AgentOffices|undefined, action: Action) : AgentOffices {
    if (state == undefined)
        state = defaultAgentOffices;

    switch(action.type) {
        case ActionType.PageLoaded:
        {
            if (action.discount.agentsFilter.selectionMode === AgentSelectionMode.AllAgents)
                return state;

            const agentOffices: AgentOffices = {
                byAgent: {},
                idMap: {}
            }

            action.discount.agentsFilter.agents.forEach(a => {
                let agentEntity = action.data.agents.find(e => e.id == a.agentId);
                if (!agentEntity)
                    return;

                const selectedOffices = new Set(a.offices);
                const codes : string[] = [];
                agentOffices.byAgent[a.agentId] = codes;

                agentEntity.offices.forEach(o => {
                    codes.push(o.id);
                    agentOffices.idMap[o.id] = {
                        isSelected: a.allOffices || selectedOffices.has(o.id),
                        office: o
                    };
                })
            });

            return agentOffices;
        }

        case ActionType.AgentOfficesLoaded:
        {
            const newIdMap = { ... state.idMap };
            const officeIds : string[] = [];
            const isSelected = action.isSelected;
            action.offices.forEach(office => {
                newIdMap[office.id] = {office, isSelected};
                officeIds.push(office.id);
            })

            const byAgent = { 
                ... state.byAgent,
                [action.agentId]: officeIds
             }

            return {
                idMap: newIdMap,
                byAgent: byAgent
            };
        }

        case ActionType.ChangeAgentSelection:
        {
            const officeIds = state.byAgent[action.id];
            if (!officeIds)
                return state;

            const idMap = { ... state.idMap }
            officeIds.forEach(id => 
                idMap[id] = { office: idMap[id].office, isSelected: action.isSelected});

            return {
                ... state,
                idMap: idMap
            }
        }

        case ActionType.ChangeAgentOfficeSelection:
        {
            const newRec = {...state.idMap[action.officeId], isSelected: action.isSelected }
            const idMap = { ... state.idMap, [action.officeId]: newRec }

            return {
                ... state,
                idMap: idMap
            }
        }
            
        default:
            return state;
    }
}

function treeState(state: SubtreeStateMap|undefined, action: Action) : SubtreeStateMap {
    if (state == undefined)
        state = {};

    switch(action.type) {

        case ActionType.PageLoaded:
            const newState : SubtreeStateMap = {};
            if (action.discount.agentsFilter.selectionMode !== AgentSelectionMode.AllAgents) {
                action.discount.agentsFilter.agents.forEach(a => 
                    newState[a.agentId] = SubtreeState.Closed
                );
            }
            return newState;

        case ActionType.AgentSearchResultSelected:
            if (state[action.agent.id])
                return state;
            return {
                ... state,
                [action.agent.id]: SubtreeState.Loading
            };

        case ActionType.AgentOfficesLoaded:
            return {
                ... state,
                [action.agentId]: SubtreeState.Closed
            };

        case ActionType.AgentOfficesLoadFailed:
            return {
                ... state,
                [action.agentId]: SubtreeState.Errored
            }

        case ActionType.ShowOrHideAgentOffices:
            const currentValue = state[action.agentId];
            if (currentValue === undefined || currentValue === SubtreeState.Errored || currentValue === SubtreeState.Loading)
                return state;
            return {
                ... state,
                [action.agentId]: action.show ? SubtreeState.Open : SubtreeState.Closed
            }


        default:
            return state;
    }
}

function selectionMode(state: AgentSelectionMode|undefined, action: Action) {
    if (state === undefined)
        state = AgentSelectionMode.AllAgents;
    
    switch(action.type) {
        case ActionType.PageLoaded:
            if (action.discount.agentsFilter.selectionMode !== undefined) {
                return action.discount.agentsFilter.selectionMode;
            }
            if ((action.discount.agentsFilter as any).allAgents !== undefined) {
                return (action.discount.agentsFilter as any).allAgents 
                    ? AgentSelectionMode.AllAgents 
                    : AgentSelectionMode.IncludeSpecific;
            }
            return AgentSelectionMode.AllAgents;
        case ActionType.ChangeAgentSelectionMode:
            return action.mode;
        default:
            return state;
    }
}

export const reducer = combineReducers({
    search,
    agents,
    treeState,
    offices,
    selectionMode
});